/* eslint-disable max-len */
import React, { useCallback, useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { Flex, Text, Toast, Label, Pane, TextInput, Button } from '@leverege/ui-elements'
import { Auth } from '@leverege/api-redux'
import I18N from '@leverege/i18n'
import { Validator } from '@leverege/validator'
import { PasswordValidatorFactory } from '@leverege/api-validator'

import { GlobalState } from '@leverege/ui-redux'
import useForm from './useForm'
import FieldErrorsComponent from './FieldErrorsComponent'

const { t } = I18N.ns( 'molten.screens.login.ChangePasswordForm' ) 

function ChangePasswordForm( props ) {
  const { dispatch, onSubmit : pOnSubmit } = props
  const [ passwordValidator, setPasswordValidator ] = useState()
  const [ passwordErrors, setPasswordErrors ] = useState()

  useEffect( () => {
    GlobalState.dispatch( ( dispatch, getState, { api } ) => {
      api
        .project( 'shared' )
        .passwordRequirements()
        .get()
        .then( ( reqs ) => {
          setPasswordValidator( PasswordValidatorFactory.create( reqs ) )
        } )
    } )
  }, [] )

  const submit = useCallback( async ( data ) => {
    const { current, new1 } = data
    
    try {
      await dispatch( Auth.changePassword( current, new1 ) )
      Toast.success( t( 'successPasswordChange' ) )
      pOnSubmit()
    } catch ( ex ) {
      if ( ex.resultCodes ) {
        setPasswordErrors( Object.values( ex.resultCodes ) )
      }
      Toast.error( t( 'errorPasswordChange' ) )
    }
  }, [ dispatch, pOnSubmit ] )

  const pOnChange = useCallback( () => {
    setPasswordErrors( null )
  }, [] )

  const {
    errors,
    values,
    onChange,
    onSubmit
  } = useForm( {
    fields : {
      current : {
        validator : useCallback( ( value, state ) => {
          const valid = Validator.required( value )

          return valid || 'Current password is required'
        }, [] )
      },
      new1 : {
        validator : useCallback( ( value, state ) => {
          if ( !passwordValidator ) {
            return true
          }

          const validationResult = passwordValidator.validate( value )

          return validationResult.valid ? true : Object.values( validationResult.errors )
        }, [ passwordValidator ] )
      },
      new2 : {
        validator : useCallback( ( value, state ) => {
          if ( !value ) {
            return 'Password confirmation is required'
          }

          if ( value !== state.new1 ) {
            return 'Password confirmation does not match password'
          }

          return true
        }, [] )
      }
    },
    onSubmit : submit,
    onChange : pOnChange
  } )

  return (
    <Flex variant="colL">
      <Flex variant="colL">
        <Flex variant="setting|colM">
          <Label variant="inputTitle|title">
            {t( 'currentPassword' )}
          </Label>
          <TextInput
            autoComplete="off"
            variant="password|inputGrow"
            hint={t( 'currentPasswordHint' )}
            type="password"
            eventData="current"
            onChange={onChange}
            value={values.current} />
          <FieldErrorsComponent errors={errors.current} />
        </Flex>
      </Flex>
      <Flex variant="colL">
        <Flex variant="setting|colM">
          <Label variant="inputTitle|title">
            {t( 'newPassword' )}
          </Label>
          <TextInput
            autoComplete="off"
            variant="password|inputGrow"
            hint={t( 'newPasswordHint' )}
            type="password"
            eventData="new1"
            onChange={onChange}
            value={values.new1}
            changeOnReturn={false} /> 
          <PasswordErrors
            value={values.new1}
            validator={passwordValidator}
            errors={passwordErrors || errors.new1} />
          <TextInput
            autoComplete="off"
            variant="password|inputGrow"
            hint={t( 'newPasswordHintReenter' )}
            type="password"
            eventData="new2"
            onChange={onChange}
            value={values.nwe2}
            changeOnReturn={false} />
          <FieldErrorsComponent errors={errors.new2} />
        </Flex>
      </Flex>
      <Flex variant="colL">
        <Flex justify="space-between">
          <Button
            variant="settings"
            onClick={onSubmit}>
            {t( 'saveNewPassword' )}
          </Button>
        </Flex>
      </Flex>
    </Flex>
  )
}

function PasswordErrors( props ) {
  const { value, errors, validator } = props
  
  const requirements = validator?.getRequirements()

  if ( !requirements ) {
    return null
  }

  const e = Object
    .values( requirements )
    .map( ( requirement ) => {
      const { type : code, ...rest } = requirement

      if ( errors != null ) {
        const error = errors.find( err => err.type === code )
        const isError = !error?.valid

        return (
          <Text
            key={code}
            variant={isError ? 'passwordRequirementError' : 'passwordRequirementSuccess'}>
            {I18N.t( code, rest )}
          </Text>
        )
      }

      if ( !value ) {
        return <Text key={code} variant="passwordRequirement">{I18N.t( code, rest )}</Text>
      }

      return <Text key={code} variant="passwordRequirementSuccess">{I18N.t( code, rest )}</Text>
    } )

  return (
    <Pane variant="passwordRequirements" layout="flex:colM">
      {e}
    </Pane>
  )
}

export default connect(
  ( state ) => {
    return {
      auth : state.auth,
      location : state.router.location.pathname
    } 
  }
)( ChangePasswordForm )
